/**
 * @graphql cc
 */
import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { GlobalQuestionsListMacro } from '../src/QuestionList/GlobalQuestionsListMacro';
import { FlagsProvider } from '../src/FlagsProvider';
import { GlobalSpaceQuery } from '../src/graphql/GlobalSpaceQuery.graphql';
import type {
	GlobalSpaceQuery$data,
	GlobalSpaceQuery$variables,
} from '../src/graphql/__generated__/GlobalSpaceQuery.graphql';

type GlobalQuestionsListMacroHandlerProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams: any;
};

const styles = cssMap({
	centerContainer: {
		textAlign: 'center',
		minHeight: '200px',
	},
});

export const GlobalQuestionsListMacroHandler: React.FC<GlobalQuestionsListMacroHandlerProps> = ({
	macroParams,
}) => {
	const { cloudId } = useSessionData();

	const { data } = useQueryInterop<GlobalSpaceQuery$data, GlobalSpaceQuery$variables>(
		GlobalSpaceQuery,
		{
			variables: {
				cloudId: cloudId!,
			},
			skip: !cloudId,
			fetchPolicy: 'cache-first',
		},
	);

	const limit = macroParams?.limit?.value ? parseInt(macroParams.limit.value, 10) : 10;
	const filter = macroParams?.filter?.value?.toLowerCase() || 'popular';
	const space = macroParams?.space?.value;
	const includeTopics = macroParams?.['include-topics']?.value || 'all';
	const topicsString = macroParams?.topic?.value || '';

	const topics = topicsString
		? topicsString
				.split(',')
				.map((topic: string) => topic.trim())
				.filter(Boolean)
		: [];

	// Check access permission
	if (!data?.confluence_questionsConfiguration?.hasConfluenceQuestionsAccess) {
		return (
			<Box xcss={styles.centerContainer}>
				<Text>
					You do not have the correct permissions to view Questions. Please request access from your
					Atlassian Cloud administrator.
				</Text>
			</Box>
		);
	}

	return (
		<FlagsProvider>
			<GlobalQuestionsListMacro
				limit={limit}
				filter={filter}
				space={space}
				includeTopics={includeTopics}
				topics={topics}
			/>
		</FlagsProvider>
	);
};
